import { RandInt } from "./plateGame.ts";

/*
 * The Baker Street Math Crossword (ADR 0134): a lattice whose lines are number
 * sentences, across and down, with some of the numbers missing. Every number sits in
 * one line across and one down (the corner and the results sit in one or two), so a
 * blank has to satisfy both. A case is kept only if it can be solved one forced step at
 * a time, and that chain of steps is the hint.
 *
 * Pure, like shopGame.ts and runnerGame.ts - no timers, no DOM, no React. Randomness
 * comes in as `randInt`, inclusive at both ends like `getRandomInt`, so tests can fix
 * it. Every function returns a new state and reads nothing but its arguments.
 *
 * A puzzle is plain data: the cells (lattice position of each number), and a list of
 * lines (the cells it reads through, its operators, the last cell being its result).
 * A third lattice is therefore a new `buildLattice` input and a rank row, not new code.
 *
 * The solver tries every value from 0 to SEARCH_MAX in the one blank of a line, so it
 * needs no algebra per operator and rejects what algebra would miss (`0 x ? = 0`).
 * Division must be exact and no running value may drop below zero; that is the whole
 * meaning of "true" for a line, at every rank.
 *
 * Text is not made here. A hint is a `Deduction` - the cell, the value and the line -
 * and `crosswordText.ts` puts it in the player's language.
 */

export type CrosswordRank = "constable" | "inspector" | "detective";

export const CROSSWORD_RANKS: CrosswordRank[] = ["constable", "inspector", "detective"];

export type CrosswordOp = "+" | "−" | "×" | "÷";

export interface RankRules {
    /** Lattice size: 5 (three-number lines) or 7 (four-number lines). */
    size: 5 | 7;
    /** Blanks in a case. */
    blanks: number;
    /** Whether the number bank is shown. */
    bank: boolean;
    /** The operators a line may use. */
    ops: CrosswordOp[];
    /** The largest number in any cell. */
    maxNumber: number;
}

export const RANK_RULES: Record<CrosswordRank, RankRules> = {
    constable: { size: 5, blanks: 4, bank: true, ops: ["+", "−"], maxNumber: 20 },
    inspector: { size: 5, blanks: 5, bank: true, ops: ["+", "−", "×", "÷"], maxNumber: 100 },
    detective: { size: 7, blanks: 6, bank: false, ops: ["+", "−", "×", "÷"], maxNumber: 150 },
};

/** Cases in a casebook, and the glasses each is worth at most. */
export const CASEBOOK_CASES = 5;
export const GLASSES_PER_CASE = 3;
export const MAX_GLASSES = CASEBOOK_CASES * GLASSES_PER_CASE;

/** The solver tries 0 to this in a line's one blank (ADR 0134 §2). */
export const SEARCH_MAX = 9999;

/** Different case titles and briefs in crosswordText.ts; a casebook draws five of them. */
export const CASE_TEXT_COUNT = 12;

/** Draws tried for one case before the best one so far is used. A real draw succeeds in a handful. */
const MAX_DRAWS = 400;

// ── The lattice ─────────────────────────────────────────────────────────────

export interface CrosswordCell {
    /** Position on the lattice: numbers sit at even rows and columns. */
    row: number;
    col: number;
}

export type CrosswordDirection = "across" | "down";

export interface CrosswordLine {
    /** "A1" to "A3", "D1" to "D3". */
    id: string;
    direction: CrosswordDirection;
    /** 1 to 3, top to bottom and left to right. */
    number: number;
    /** Cell indexes: the operands in order, then the result. */
    cells: number[];
    /** One fewer than the operands. */
    ops: CrosswordOp[];
}

interface LineShape {
    direction: CrosswordDirection;
    number: number;
    cells: number[];
}

interface Lattice {
    size: number;
    cells: CrosswordCell[];
    lines: LineShape[];
}

const lattices = new Map<number, Lattice>();

/**
 * Number cells at the even rows and columns; three lines each way, on rows and columns
 * 0, 2 and 4. On the 7 x 7 lattice the bottom-right corner is empty: nothing would end
 * both the last line across and the last line down.
 */
export function buildLattice(size: number): Lattice {
    const cached = lattices.get(size);
    if (cached) {
        return cached;
    }
    const cells: CrosswordCell[] = [];
    const at = new Map<string, number>();
    for (let row = 0; row < size; row += 2) {
        for (let col = 0; col < size; col += 2) {
            if (row <= 4 || col <= 4) {
                at.set(`${row},${col}`, cells.length);
                cells.push({ row, col });
            }
        }
    }
    const lines: LineShape[] = [];
    for (const direction of ["across", "down"] as const) {
        for (let n = 0; n < 3; n++) {
            const members: number[] = [];
            for (let k = 0; k < size; k += 2) {
                members.push(at.get(direction === "across" ? `${2 * n},${k}` : `${k},${2 * n}`)!);
            }
            lines.push({ direction, number: n + 1, cells: members });
        }
    }
    const built = { size, cells, lines };
    lattices.set(size, built);
    return built;
}

export interface CrosswordPuzzle {
    rank: CrosswordRank;
    size: 5 | 7;
    cells: CrosswordCell[];
    lines: CrosswordLine[];
    /** The answer in every cell. */
    solution: number[];
    /** The hidden cells, in reading order. */
    blanks: number[];
}

// ── Arithmetic ──────────────────────────────────────────────────────────────

/**
 * The value of `nums[0] ops[0] nums[1] ...`, times and divide before add and take away,
 * left to right inside each. Null if a division is not exact (or by zero), if a running
 * value drops below zero, or if any term or running value passes `cap`.
 */
export function evaluate(ops: readonly CrosswordOp[], nums: readonly number[], cap = Infinity): number | null {
    let acc = 0;
    let pending: "+" | "−" = "+";
    let term = nums[0];
    if (term > cap) {
        return null;
    }
    for (let i = 0; i < ops.length; i++) {
        const op = ops[i];
        const next = nums[i + 1];
        if (op === "×") {
            term *= next;
            if (term > cap) {
                return null;
            }
        } else if (op === "÷") {
            if (next === 0 || term % next !== 0) {
                return null;
            }
            term /= next;
        } else {
            acc = pending === "+" ? acc + term : acc - term;
            if (acc < 0 || acc > cap) {
                return null;
            }
            pending = op;
            term = next;
        }
    }
    acc = pending === "+" ? acc + term : acc - term;
    return acc < 0 || acc > cap ? null : acc;
}

/** True if the line, with `values` in its cells, is a true sentence. */
export function lineHolds(line: CrosswordLine, values: readonly number[]): boolean {
    const operands = line.cells.slice(0, -1).map((cell) => values[cell]);
    return evaluate(line.ops, operands) === values[line.cells[line.cells.length - 1]];
}

/**
 * Every whole number from 0 to SEARCH_MAX that makes the line true with `nums` in its
 * other places (`nums[unknown]` is ignored). Stops at `limit`: callers want to know
 * "none", "one" or "more than one".
 */
export function valuesFor(ops: readonly CrosswordOp[], nums: readonly number[], unknown: number, limit = 2): number[] {
    const found: number[] = [];
    const last = nums.length - 1;
    if (unknown === last) {
        const result = evaluate(ops, nums.slice(0, last));
        return result !== null && result <= SEARCH_MAX ? [result] : [];
    }
    const probe = nums.slice(0, last);
    const target = nums[last];
    for (let value = 0; value <= SEARCH_MAX; value++) {
        probe[unknown] = value;
        if (evaluate(ops, probe) === target) {
            found.push(value);
            if (found.length >= limit) {
                break;
            }
        }
    }
    return found;
}

/**
 * Fills in every blank that a chain of forced steps reaches, and returns whether that is
 * all of them. `known` is changed in place; null is a blank. Each step takes a line with
 * exactly one blank and requires that blank to have exactly one value.
 */
function deduce(lines: readonly CrosswordLine[], known: (number | null)[], memo?: Map<string, number[]>): boolean {
    let progress = true;
    while (progress) {
        progress = false;
        for (const line of lines) {
            let unknown = -1;
            let count = 0;
            for (let i = 0; i < line.cells.length; i++) {
                if (known[line.cells[i]] === null) {
                    count++;
                    unknown = i;
                }
            }
            if (count !== 1) {
                continue;
            }
            const nums = line.cells.map((cell) => known[cell] ?? 0);
            // Hiding one number after another asks about the same line with the same numbers again and again.
            const key = `${line.id}:${nums.join(",")}:${unknown}`;
            let values = memo?.get(key);
            if (!values) {
                values = valuesFor(line.ops, nums, unknown);
                memo?.set(key, values);
            }
            if (values.length === 1) {
                known[line.cells[unknown]] = values[0];
                progress = true;
            }
        }
    }
    return known.every((value) => value !== null);
}

/** True if hiding exactly `blanks` leaves a puzzle solved by forced steps from the rest. */
export function deducible(puzzle: Pick<CrosswordPuzzle, "lines" | "solution">, blanks: readonly number[]): boolean {
    const known = puzzle.solution.map((value, cell) => (blanks.includes(cell) ? null : value));
    return deduce(puzzle.lines, known);
}

// ── Drawing a full lattice ──────────────────────────────────────────────────

const pick = <T>(items: readonly T[], randInt: RandInt): T => items[randInt(0, items.length - 1)];

const shuffled = <T>(items: readonly T[], randInt: RandInt): T[] => {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = randInt(0, i);
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
};

const HIGH: CrosswordOp[] = ["×", "÷"];

/** The result of `x op y` on the small lattice if the line is fit for the rank, else null. */
function smallLine(rank: CrosswordRank, op: CrosswordOp, x: number, y: number): number | null {
    const z = evaluate([op], [x, y]);
    const max = RANK_RULES[rank].maxNumber;
    if (z === null || x > max || y > max || z > max) {
        return null;
    }
    if (op === "+") {
        // Adding nothing teaches nothing.
        return x >= 1 && y >= 1 ? z : null;
    }
    if (op === "−") {
        return y >= 1 ? z : null;
    }
    // Times and divide stay inside the 10 x 10 facts, and never by 0 or 1 (ADR 0134 §3).
    if (op === "×") {
        return x >= 2 && x <= 10 && y >= 2 && y <= 10 ? z : null;
    }
    return y >= 2 && y <= 10 && z >= 2 && z <= 10 ? z : null;
}

interface Draw {
    values: number[];
    ops: CrosswordOp[][];
}

/**
 * A 5 x 5 lattice: draw the operators and three of the four top-left numbers, then try
 * every value for the fourth and keep one that makes the corner agree. The other five
 * numbers follow from the lines. Null if no value does.
 */
function drawSmall(rank: CrosswordRank, times: boolean, randInt: RandInt): Draw | null {
    const rules = RANK_RULES[rank];
    let ops: CrosswordOp[];
    let low = 1;
    let cap = 15;
    if (times) {
        // Times and divide only close at the corner when the rows and columns pair up: the first and
        // last line across are alike, the first and last down are alike, and the middle two follow.
        const sign = (op: CrosswordOp) => (op === "×" ? 1 : -1);
        const [across, middle, down] = [pick(HIGH, randInt), pick(HIGH, randInt), pick(HIGH, randInt)];
        const down2 = sign(down) * sign(middle) * sign(across) === 1 ? "×" : "÷";
        ops = [across, middle, across, down, down2, down];
        low = 2;
        cap = randInt(5, 10);
    } else {
        const family = rank === "inspector" ? (["+", "−"] as CrosswordOp[]) : rules.ops;
        ops = Array.from({ length: 6 }, () => pick(family, randInt));
        cap = rank === "inspector" ? 50 : 15;
    }
    const [a1, a2, a3, d1, d2, d3] = ops;
    const a = randInt(low, cap);
    const b = randInt(low, cap);
    const d = randInt(low, cap);
    const c = smallLine(rank, a1, a, b);
    const g = smallLine(rank, d1, a, d);
    if (c === null || g === null) {
        return null;
    }
    const fits: { e: number; f: number; h: number; corner: number }[] = [];
    for (let e = 1; e <= rules.maxNumber; e++) {
        const f = smallLine(rank, a2, d, e);
        const h = smallLine(rank, d2, b, e);
        if (f === null || h === null) {
            continue;
        }
        const across = smallLine(rank, a3, g, h);
        const down = smallLine(rank, d3, c, f);
        if (across !== null && across === down) {
            fits.push({ e, f, h, corner: across });
        }
    }
    if (fits.length === 0) {
        return null;
    }
    const { e, f, h, corner } = pick(fits, randInt);
    return { values: [a, b, c, d, e, f, g, h, corner], ops: [[a1], [a2], [a3], [d1], [d2], [d3]] };
}

const DETECTIVE_FACTOR_MAX = 12;

/** Whether a four-number line with `nums` filled in (a prefix of its operands) can still come true. */
function detectiveLineOk(ops: readonly CrosswordOp[], operands: readonly number[]): boolean {
    for (let i = 0; i < ops.length; i++) {
        // Never times or divide by 1 (ADR 0134 §3).
        if (HIGH.includes(ops[i]) && (operands[i] < 2 || operands[i + 1] < 2)) {
            return false;
        }
    }
    const result = evaluate(ops, operands, RANK_RULES.detective.maxNumber);
    return result !== null && result >= 1;
}

function canComplete(ops: readonly CrosswordOp[], prefix: readonly number[]): boolean {
    if (prefix.length === 3) {
        return detectiveLineOk(ops, prefix);
    }
    for (let next = 1; next <= DETECTIVE_FACTOR_MAX; next++) {
        if (canComplete(ops, [...prefix, next])) {
            return true;
        }
    }
    return false;
}

/**
 * A 7 x 7 lattice: nine numbers 1 to 12 in reading order, each chosen so that the lines
 * through it can still come true, and each line one times-or-divide and one add-or-take-away
 * so that the order of operations decides it. The six results follow; nothing needs to agree.
 */
function drawLarge(randInt: RandInt): Draw | null {
    const ops: CrosswordOp[][] = Array.from({ length: 6 }, () => {
        const high = randInt(0, 2) === 0 ? "÷" : "×";
        const low = randInt(0, 1) === 0 ? "+" : "−";
        return randInt(0, 1) === 0 ? [high, low] : [low, high];
    });
    const inner: number[][] = [[], [], []];
    for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 3; col++) {
            const acrossPrefix = inner[row];
            const options: number[] = [];
            for (let value = 1; value <= DETECTIVE_FACTOR_MAX; value++) {
                const down = [...inner.slice(0, row).map((r) => r[col]), value];
                if (canComplete(ops[row], [...acrossPrefix, value]) && canComplete(ops[3 + col], down)) {
                    options.push(value);
                }
            }
            if (options.length === 0) {
                return null;
            }
            inner[row].push(pick(options, randInt));
        }
    }
    const values: number[] = [];
    const acrossResults = inner.map((r, i) => evaluate(ops[i], r)!);
    const downResults = [0, 1, 2].map((col) => evaluate(ops[3 + col], [inner[0][col], inner[1][col], inner[2][col]])!);
    // Reading order: three rows of (three inner, one result), then the row of down results.
    for (let row = 0; row < 3; row++) {
        values.push(...inner[row], acrossResults[row]);
    }
    values.push(...downResults);
    return { values, ops };
}

/** Hides numbers in random order, keeping each hide only if the case is still solved by forced steps. */
function hide(lines: readonly CrosswordLine[], values: readonly number[], count: number, randInt: RandInt): number[] {
    const blanks: number[] = [];
    const memo = new Map<string, number[]>();
    for (const cell of shuffled(values.map((_, i) => i), randInt)) {
        if (blanks.length === count) {
            break;
        }
        const known: (number | null)[] = values.map((value, i) => (i === cell || blanks.includes(i) ? null : value));
        if (deduce(lines, known, memo)) {
            blanks.push(cell);
        }
    }
    return blanks.sort((x, y) => x - y);
}

function assemble(rank: CrosswordRank, draw: Draw): Omit<CrosswordPuzzle, "blanks"> {
    const lattice = buildLattice(RANK_RULES[rank].size);
    return {
        rank,
        size: RANK_RULES[rank].size,
        cells: lattice.cells,
        lines: lattice.lines.map((shape, i) => ({
            id: `${shape.direction === "across" ? "A" : "D"}${shape.number}`,
            ...shape,
            ops: draw.ops[i],
        })),
        solution: draw.values,
    };
}

/** A puzzle from its numbers (in cell order), each line's operators and its blanks: how tests and hand-made cases are built. */
export function makePuzzle(
    rank: CrosswordRank,
    values: readonly number[],
    ops: readonly (readonly CrosswordOp[])[],
    blanks: readonly number[]
): CrosswordPuzzle {
    return {
        ...assemble(rank, { values: [...values], ops: ops.map((line) => [...line]) }),
        blanks: [...blanks].sort((x, y) => x - y),
    };
}

/** One case at the rank: drawn, checked for range and truth, then hidden down to the rank's blank count. */
export function generatePuzzle(rank: CrosswordRank, randInt: RandInt): CrosswordPuzzle {
    const rules = RANK_RULES[rank];
    let best: CrosswordPuzzle | null = null;
    // An Inspector case is all adding and taking away, or all times and divide: the corner only agrees within one.
    const times = rank === "inspector" && randInt(0, 1) === 1;
    for (let attempt = 0; attempt < MAX_DRAWS; attempt++) {
        const draw = rank === "detective" ? drawLarge(randInt) : drawSmall(rank, times, randInt);
        if (!draw) {
            continue;
        }
        const base = assemble(rank, draw);
        const blanks = hide(base.lines, base.solution, rules.blanks, randInt);
        if (blanks.length === rules.blanks) {
            return { ...base, blanks };
        }
        if (!best || blanks.length > best.blanks.length) {
            best = { ...base, blanks };
        }
    }
    if (!best) {
        throw new Error(`Could not draw a ${rank} crossword`);
    }
    return best;
}

// ── The number bank ─────────────────────────────────────────────────────────

/** The missing numbers, smallest first (ADR 0134 §3). */
export function bankFor(puzzle: CrosswordPuzzle): number[] {
    return puzzle.blanks.map((cell) => puzzle.solution[cell]).sort((x, y) => x - y);
}

/**
 * Which bank numbers are struck off: each number placed in a blank strikes one matching
 * bank entry, so a number that is missing twice is struck twice (a multiset, not a set).
 */
export function bankUsed(puzzle: CrosswordPuzzle, entries: readonly (number | null)[]): boolean[] {
    const placed = new Map<number, number>();
    for (const cell of puzzle.blanks) {
        const value = entries[cell];
        if (value !== null && value !== undefined) {
            placed.set(value, (placed.get(value) ?? 0) + 1);
        }
    }
    return bankFor(puzzle).map((value) => {
        const left = placed.get(value) ?? 0;
        if (left > 0) {
            placed.set(value, left - 1);
            return true;
        }
        return false;
    });
}

// ── Reading a line ──────────────────────────────────────────────────────────

/** "6 × ? = 42": a line written out, with `show` giving what is in each cell ("?" for a blank). */
export function lineSentence(line: CrosswordLine, show: (cell: number) => string): string {
    const parts: string[] = [];
    line.cells.forEach((cell, i) => {
        if (i > 0) {
            parts.push(i < line.cells.length - 1 ? line.ops[i - 1] : "=");
        }
        parts.push(show(cell));
    });
    return parts.join(" ");
}

/** Where a cell sits in each line it belongs to. */
export function linesOfCell(puzzle: CrosswordPuzzle, cell: number): { line: CrosswordLine; position: number }[] {
    return puzzle.lines.flatMap((line) => {
        const position = line.cells.indexOf(cell);
        return position >= 0 ? [{ line, position }] : [];
    });
}

// ── Asking Holmes ───────────────────────────────────────────────────────────

export interface Deduction {
    cell: number;
    value: number;
    /** Index into `puzzle.lines`. */
    line: number;
}

/**
 * The next forced step from what the child has right (givens and right entries; a wrong
 * entry counts as empty): a line with exactly one unknown that has exactly one value. A step
 * for `prefer` (the selected blank) is taken if there is one. Null when everything is known.
 *
 * There is always a step while a blank is unknown: the chain that made the case solvable
 * from the givens still works from any larger known set.
 */
export function nextDeduction(
    puzzle: CrosswordPuzzle,
    entries: readonly (number | null)[],
    prefer?: number | null
): Deduction | null {
    const known = puzzle.solution.map((value, cell) =>
        puzzle.blanks.includes(cell) && entries[cell] !== value ? null : value
    );
    let first: Deduction | null = null;
    for (let index = 0; index < puzzle.lines.length; index++) {
        const line = puzzle.lines[index];
        const unknowns = line.cells.filter((cell) => known[cell] === null);
        if (unknowns.length !== 1) {
            continue;
        }
        const position = line.cells.indexOf(unknowns[0]);
        const values = valuesFor(line.ops, line.cells.map((cell) => known[cell] ?? 0), position);
        if (values.length !== 1) {
            continue;
        }
        const step = { cell: unknowns[0], value: values[0], line: index };
        if (step.cell === prefer) {
            return step;
        }
        first = first ?? step;
    }
    return first;
}

// ── A casebook ──────────────────────────────────────────────────────────────

/** Glasses for a case: 3, less one for each hint and each check that found a wrong blank, never below 1. */
export function glassesFor(hints: number, wrongChecks: number): number {
    return Math.max(1, GLASSES_PER_CASE - hints - wrongChecks);
}

export interface CaseState {
    puzzle: CrosswordPuzzle;
    /** What is in each cell; givens hold their number, blanks the child's entry or null. */
    entries: (number | null)[];
    /** Blanks Holmes filled. */
    helped: boolean[];
    /** Blanks the last check found wrong, until they are changed. */
    marked: number[];
    hints: number;
    wrongChecks: number;
    wrongFound: number;
}

export type CasebookPhase = "playing" | "solved" | "over";

export interface CasebookState {
    rank: CrosswordRank;
    /** 1 to CASEBOOK_CASES. */
    caseNumber: number;
    /** The text index of each case's title and brief; five different. */
    textOrder: number[];
    current: CaseState;
    phase: CasebookPhase;
    /** Glasses won in closed cases. */
    glasses: number;
    /** Closed cases solved without Ask Holmes. */
    unaided: number;
    /** Blanks the child filled right, blanks found wrong by Check, and blanks Holmes filled; closed cases only. */
    correct: number;
    wrong: number;
    skipped: number;
}

export interface CrosswordResult {
    rank: CrosswordRank;
    glasses: number;
    unaided: number;
    correct: number;
    wrong: number;
    skipped: number;
}

/** A case with nothing filled in yet. */
export function caseFor(puzzle: CrosswordPuzzle): CaseState {
    return {
        puzzle,
        entries: puzzle.solution.map((value, cell) => (puzzle.blanks.includes(cell) ? null : value)),
        helped: puzzle.solution.map(() => false),
        marked: [],
        hints: 0,
        wrongChecks: 0,
        wrongFound: 0,
    };
}

function startCase(rank: CrosswordRank, randInt: RandInt): CaseState {
    return caseFor(generatePuzzle(rank, randInt));
}

export function startCasebook(rank: CrosswordRank, randInt: RandInt): CasebookState {
    const order = shuffled(Array.from({ length: CASE_TEXT_COUNT }, (_, i) => i), randInt).slice(0, CASEBOOK_CASES);
    return {
        rank,
        caseNumber: 1,
        textOrder: order,
        current: startCase(rank, randInt),
        phase: "playing",
        glasses: 0,
        unaided: 0,
        correct: 0,
        wrong: 0,
        skipped: 0,
    };
}

export function isSolved(c: CaseState): boolean {
    return c.puzzle.blanks.every((cell) => c.entries[cell] === c.puzzle.solution[cell]);
}

/** Glasses the open case is worth right now. */
export function caseWorth(c: CaseState): number {
    return glassesFor(c.hints, c.wrongChecks);
}

/** Closes the open case if every blank is right: adds its glasses and counts. */
function closeIfSolved(state: CasebookState): CasebookState {
    const c = state.current;
    if (state.phase !== "playing" || !isSolved(c)) {
        return state;
    }
    const helped = c.puzzle.blanks.filter((cell) => c.helped[cell]).length;
    return {
        ...state,
        phase: "solved",
        glasses: state.glasses + caseWorth(c),
        unaided: state.unaided + (c.hints === 0 ? 1 : 0),
        correct: state.correct + c.puzzle.blanks.length - helped,
        wrong: state.wrong + c.wrongFound,
        skipped: state.skipped + helped,
    };
}

/** Puts `value` (null clears) in a blank. The case closes by itself when the last blank is right. */
export function enterNumber(state: CasebookState, cell: number, value: number | null): CasebookState {
    const c = state.current;
    if (state.phase !== "playing" || !c.puzzle.blanks.includes(cell) || c.helped[cell]) {
        return state;
    }
    const entries = [...c.entries];
    entries[cell] = value;
    const next: CasebookState = {
        ...state,
        current: { ...c, entries, marked: c.marked.filter((marked) => marked !== cell) },
    };
    return closeIfSolved(next);
}

/** Blanks that hold a number which is not the answer. */
export function wrongBlanks(c: CaseState): number[] {
    return c.puzzle.blanks.filter((cell) => c.entries[cell] !== null && c.entries[cell] !== c.puzzle.solution[cell]);
}

/**
 * Marks every wrong entry. A check that finds one costs a glass; one that finds none, or
 * has nothing to look at, costs nothing (ADR 0134 §4).
 */
export function checkCase(state: CasebookState): { state: CasebookState; wrongCount: number; filled: number } {
    const c = state.current;
    const wrong = wrongBlanks(c);
    const filled = c.puzzle.blanks.filter((cell) => c.entries[cell] !== null).length;
    if (state.phase !== "playing" || wrong.length === 0) {
        return { state, wrongCount: 0, filled };
    }
    return {
        state: {
            ...state,
            current: { ...c, marked: wrong, wrongChecks: c.wrongChecks + 1, wrongFound: c.wrongFound + wrong.length },
        },
        wrongCount: wrong.length,
        filled,
    };
}

/** Holmes fills one blank and costs a glass. `prefer` is the selected blank, taken if a forced step reaches it. */
export function askHolmes(
    state: CasebookState,
    prefer?: number | null
): { state: CasebookState; deduction: Deduction | null } {
    const c = state.current;
    const deduction = state.phase === "playing" ? nextDeduction(c.puzzle, c.entries, prefer) : null;
    if (!deduction) {
        return { state, deduction: null };
    }
    const entries = [...c.entries];
    entries[deduction.cell] = deduction.value;
    const helped = [...c.helped];
    helped[deduction.cell] = true;
    const next: CasebookState = {
        ...state,
        current: {
            ...c,
            entries,
            helped,
            marked: c.marked.filter((marked) => marked !== deduction.cell),
            hints: c.hints + 1,
        },
    };
    return { state: closeIfSolved(next), deduction };
}

/** The next case, or the end of the casebook after the fifth. */
export function nextCase(state: CasebookState, randInt: RandInt): CasebookState {
    if (state.phase !== "solved") {
        return state;
    }
    if (state.caseNumber >= CASEBOOK_CASES) {
        return { ...state, phase: "over" };
    }
    return { ...state, caseNumber: state.caseNumber + 1, current: startCase(state.rank, randInt), phase: "playing" };
}

export function resultOf(state: CasebookState): CrosswordResult {
    return {
        rank: state.rank,
        glasses: state.glasses,
        unaided: state.unaided,
        correct: state.correct,
        wrong: state.wrong,
        skipped: state.skipped,
    };
}
